'use client';

import { useEffect } from 'react';

import { usePathname, useRouter } from 'next/navigation';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { bioUrl } from '@ps/functions/bio/bioUrls';
import type { PopStoreExtendedSubscriptionLevel } from '@ps/models';
import { CreatorInfo } from '@ps/types/creator';
import {
  useCurrentUserId,
  useIsGuestUser,
  useLogin,
} from '@ps/ui/contexts/auth';

import { subscribeSuccessRedirect } from './subscribeSuccessRedirect';

interface Args {
  bioContext: BioContext;
  /** The creator profile — its avatar + name brand the login modal (parity with
   *  the bio header's login pill). */
  creatorInfo: CreatorInfo;
  defaultLevel?: PopStoreExtendedSubscriptionLevel;
  minLevel?: PopStoreExtendedSubscriptionLevel;
  successRedirectUrl?: string;
  presentation?: 'intercept' | 'standalone';
}

/**
 * Anonymous→login gate for the `/<url_code>/subscribe` surface, shared by BOTH
 * shells (the intercept dialog and the standalone page). Subscribing needs an
 * identity, so a CONFIRMED-anonymous viewer is soft-redirected to the login
 * modal, and the gate reports `blocked = true` so the shell renders NOTHING
 * while the redirect runs.
 *
 * The blocked-hides-the-whole-shell part is load-bearing: the subscribe dialog's
 * chrome is `z-[10000]` (above the bio banner + account dialog). If only the
 * inner picker hid (returning `null` inside a still-mounted dialog), the empty
 * high-z backdrop would sit ON TOP of the `/login` overlay and hide it. Hiding
 * the shell entirely keeps login clean.
 *
 * `blocked` is `true` ONLY once we CONFIRM the viewer isn't a real user —
 * anonymous (`userId === null`) OR a throwaway guest (`useIsGuestUser() === true`;
 * a guest has a uid, so it must be excluded explicitly — community subscription
 * needs a real account). The resolving states (`userId === undefined`, or a present
 * user whose guest claim is still in flight) are NOT blocked — the picker paints
 * its skeleton chrome immediately while auth settles, and we never redirect a real
 * user mid-resolution.
 *
 * `router.replace` (NOT push) drops this subscribe route from history so
 * DISMISSING login returns to the bio (never back onto this gate → loop) and a
 * re-tap re-runs cleanly. On SUCCESS a login-success callback SOFT-reopens the
 * subscribe modal: the login modal's `land()` runs `router.back()` (→ the bio,
 * since `replace` dropped `/subscribe`) then the callback, which `router.push`es
 * the subscribe INTERCEPT — so the picker reappears as a modal over the live bio,
 * NOT the standalone page (the login modal's `window.location.href = next`
 * hard-nav fallback fires ONLY when the callback is gone — i.e. a cold-load /
 * refresh at `/login`). Server `requireSession` can't be used — its `redirect()`
 * is a hard nav that tears the bio down and defeats the intercept.
 */
export function useIsSubscribeBlocked({
  bioContext,
  creatorInfo,
  defaultLevel,
  minLevel,
  successRedirectUrl,
  presentation,
}: Args): boolean {
  const router = useRouter();
  const pathname = usePathname();
  const userId = useCurrentUserId();
  const isGuest = useIsGuestUser();
  const login = useLogin();

  // On the `/subscribe` route? Gates the redirect so it never fires while a
  // checkout is stacked over this picker (pathname `/checkout`).
  const isSubscribeRoute = pathname.endsWith('/subscribe');
  // The viewer needs to log in when we've CONFIRMED they aren't a real user —
  // anonymous (`userId === null`) or a throwaway guest (`isGuest === true`). Both
  // `undefined` states (auth resolving / claim in flight) leave this `false` so we
  // never redirect a real user mid-resolution.
  const needsLogin = userId === null || isGuest === true;

  const blocked =
    presentation === 'intercept' && (needsLogin || !isSubscribeRoute);

  const subscribeParams = {
    defaultLevel,
    minLevel,
    // Preserve the post-checkout return target across the login round-trip.
    redirectTo: subscribeSuccessRedirect(bioContext, successRedirectUrl),
  };
  // The SOFT return target (login-success callback): the subscribe INTERCEPT
  // (`'auto'`), reopened as a modal over the bio.
  const subscribeModalPath = bioUrl(bioContext).subscribe(subscribeParams);
  // The HARD fallback (`?next=`, used ONLY on a cold-load/refresh at `/login`):
  // the standalone subscribe page — a fresh document can't restore the modal.
  const nextPath = bioUrl(bioContext).subscribe(
    subscribeParams,
    'non-intercepting',
  );

  useEffect(() => {
    if (!needsLogin || !isSubscribeRoute) return;
    login({
      next: nextPath,
      creatorInfo,
      replace: true,
      method: presentation === 'intercept' ? 'soft' : 'hard',
      onSuccess: () => {
        // this will only be called if the login modal was opened via the intercept
        // (soft nav) — a hard nav to `/login` (cold-load / refresh) will never call
        // this callback, so the fallback `nextPath` is the standalone page.
        router.replace(subscribeModalPath);
      },
    });
  }, [
    needsLogin,
    isSubscribeRoute,
    router,
    nextPath,
    subscribeModalPath,
    login,
    creatorInfo,
    presentation,
  ]);

  return blocked;
}
